import { useEffect, useMemo, useRef, useState } from "react";
import "devextreme/dist/css/dx.fluent.blue.light.compact.css";
import { Item, Resource, Scheduler, Toolbar, type SchedulerTypes } from "devextreme-react/scheduler";
import { Draggable } from "devextreme-react/draggable";
import DataSource from "devextreme/data/data_source";
import ArrayStore from "devextreme/data/array_store";
import { Clock } from "lucide-react";
import type { EventDto } from "../../../shared/types";
import {
  useAccounts,
  useCreateEvent,
  useEventsRange,
  useSettings,
  useUpdateEvent,
} from "../../hooks/useApi";
import { providerMeta } from "../providerMeta";
import { ErrorState } from "../common/ErrorState";

// Draggable text chips in the toolbar — drop one on a slot to create an event.
const DRAG_GROUP = "calendar-palette";
const CHIPS = ["Gutter", "Selskap"];

const SCHEDULER_VIEWS: SchedulerTypes.ViewType[] = [
  "day",
  "week",
  "workWeek",
  "month",
  "timelineDay",
  "timelineWeek",
  "timelineMonth",
  "agenda",
];

interface Appointment {
  id: string;
  text: string;
  startDate: Date;
  endDate: Date;
  allDay: boolean;
  location?: string;
  accountId?: number;
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}
function endOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59);
}

/** All-day events arrive as date-only "YYYY-MM-DD" — parse to a local Date to avoid a UTC day-shift. */
function parseEventDate(value: string): Date {
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.exec(value);
  if (dateOnly) {
    const [y, m, d] = value.split("-").map(Number);
    return new Date(y!, m! - 1, d!);
  }
  return new Date(value);
}

function toAppointment(e: EventDto): Appointment {
  const start = parseEventDate(e.start);
  const end = e.end ? parseEventDate(e.end) : start;
  return {
    id: e.id,
    text: e.subject,
    startDate: start,
    endDate: end,
    allDay: e.isAllDay,
    location: e.location ?? undefined,
    accountId: e.accountId,
  };
}

export function CalendarDashboard({ accountId }: { accountId: number }) {
  const { data: settings } = useSettings();
  const { data: accounts } = useAccounts();
  const createEvent = useCreateEvent();
  const updateEvent = useUpdateEvent();
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [currentView, setCurrentView] = useState<string>("week");
  const [createError, setCreateError] = useState<{ message: string; reconnectUrl?: string } | null>(
    null,
  );
  // Title of the palette chip currently being dragged. DevExtreme doesn't carry
  // the Draggable's data into the dropped appointment, so we track it ourselves.
  const draggedText = useRef<string | null>(null);

  // Dismiss the DevExtreme trial license notice once it renders.
  useEffect(() => {
    const parent = document.querySelector("dx-license");
    if (parent) {
      const secondDiv = parent.querySelectorAll("div")[1];
      if (secondDiv) secondDiv.click();
    }
  }, []);

  // Adopt the saved default view when it loads/changes; manual switches still win.
  useEffect(() => {
    if (settings?.calendarDefaultView) setCurrentView(settings.calendarDefaultView);
  }, [settings?.calendarDefaultView]);

  // Fetch a month-anchored window (covers day/week/month views); refetch when
  // navigation moves currentDate into a new month.
  const { startISO, endISO } = useMemo(() => {
    const start = startOfMonth(currentDate);
    start.setDate(start.getDate() - 7);
    const end = endOfMonth(currentDate);
    end.setDate(end.getDate() + 7);
    return { startISO: start.toISOString(), endISO: end.toISOString() };
  }, [currentDate]);

  const { data: events, error } = useEventsRange(accountId, startISO, endISO);

  const appointments = useMemo(() => (events ?? []).map(toAppointment), [events]);

  // Wrap in a keyed store so DevExtreme can uniquely identify appointments
  // (drag/resize/agenda need this) — otherwise it warns W1023.
  const dataSource = useMemo(
    () => new DataSource({ store: new ArrayStore({ key: "id", data: appointments }) }),
    [appointments],
  );

  // Per-account pastel palette for appointment coloring.
  const accountPalette = useMemo(
    () => (accounts ?? []).map((a) => ({ id: a.id, text: a.username, color: a.color })),
    [accounts],
  );

  const onCreateError = (targetId: number) => (err: Error) => {
    const account = (accounts ?? []).find((a) => a.id === targetId);
    const reconnectUrl =
      err.name === "ReauthError" && account ? providerMeta(account.provider).loginUrl : undefined;
    setCreateError({ message: err.message, reconnectUrl });
  };

  // Shared create path for both double-click and drag-drop. Single-account view
  // is fixed to that account; the All view defaults to the first account.
  const createAt = (
    subject: string,
    start: Date,
    end: Date,
    opts?: { accountId?: number; isAllDay?: boolean; location?: string },
  ) => {
    const targetId = accountId > 0 ? accountId : (opts?.accountId ?? accounts?.[0]?.id);
    if (!targetId) return;
    setCreateError(null);
    createEvent.mutate(
      {
        accountId: targetId,
        subject: subject.trim() || "(no title)",
        start: start.toISOString(),
        end: end.toISOString(),
        isAllDay: opts?.isAllDay ?? false,
        location: opts?.location,
      },
      { onError: onCreateError(targetId) },
    );
  };

  // Open the New Appointment dialog pre-filled with the dropped chip's title.
  // Handled in appointmentDragging.onAdd (below) because that fires on drop
  // regardless of the data store; onAppointmentAdding may not fire for a drop.
  const handleChipDrop = (e: any) => {
    const text = e.fromData?.text ?? draggedText.current ?? "";
    const start = e.itemData?.startDate ? new Date(e.itemData.startDate) : new Date();
    const end = new Date(start.getTime() + 60 * 60_000); // default to a 1-hour event
    const defaultAccount = accountId > 0 ? accountId : accounts?.[0]?.id;
    e.component.showAppointmentPopup(
      { text, startDate: start, endDate: end, accountId: defaultAccount },
      true,
    );
    // Clear after the synchronous drop handlers settle, so the dialog's own
    // confirm (a later onAppointmentAdding) is treated as a normal create.
    setTimeout(() => {
      draggedText.current = null;
    }, 0);
  };

  // Fires when an appointment is added: a double-click confirm or the confirm
  // of the dialog we open on drop. Suppress the drop's own blank auto-add.
  const handleAdding = (e: SchedulerTypes.AppointmentAddingEvent) => {
    if (draggedText.current) {
      e.cancel = true; // the drop's blank auto-add — the dialog handles creation
      return;
    }
    const d = e.appointmentData as Record<string, any>;
    // Double-click / dialog confirm → persist server-side, refetch, cancel local.
    e.cancel = true;
    createAt(d.text ?? "", new Date(d.startDate), new Date(d.endDate ?? d.startDate), {
      accountId: d.accountId,
      isAllDay: Boolean(d.allDay),
      location: d.location || undefined,
    });
  };

  // Drag to move / resize to change duration → persist, refetch confirms.
  const handleUpdating = (e: SchedulerTypes.AppointmentUpdatingEvent) => {
    const old = e.oldData as Appointment;
    const next = e.newData as Appointment;
    const targetId = accountId > 0 ? accountId : old.accountId;
    if (!targetId || !old.id) return;
    updateEvent.mutate(
      {
        accountId: targetId,
        eventId: old.id,
        subject: next.text ?? old.text ?? "(no title)",
        start: new Date(next.startDate).toISOString(),
        end: new Date(next.endDate ?? next.startDate).toISOString(),
        isAllDay: Boolean(next.allDay),
        location: next.location || undefined,
      },
      {
        onError: (err) => {
          const account = (accounts ?? []).find((a) => a.id === targetId);
          const reconnectUrl =
            err.name === "ReauthError" && account
              ? providerMeta(account.provider).loginUrl
              : undefined;
          setCreateError({ message: err.message, reconnectUrl });
        },
      },
    );
  };

  // Toolbar palette: draggable text chips + a placeholder Hours button.
  const renderPalette = () => (
    <div style={{ marginLeft: 40 }} className="flex items-center gap-1.5">
      {CHIPS.map((text) => (
        <Draggable
          key={text}
          data={{ text }}
          group={DRAG_GROUP}
          clone
          className="inline-flex"
          onDragStart={() => {
            draggedText.current = text;
          }}
        >
          <span className="cursor-grab select-none rounded-full bg-accent-subtle px-2.5 py-0.5 text-[12px] font-medium text-accent active:cursor-grabbing">
            {text}
          </span>
        </Draggable>
      ))}
      <button
        type="button"
        onClick={() => {
          /* TODO: Hours behavior — defined later */
        }}
        className="ml-1 inline-flex items-center gap-1 rounded border border-stroke px-2 py-0.5 text-[12px] font-medium text-ink hover:bg-black/5"
      >
        <Clock size={13} />
        Hours
      </button>
    </div>
  );

  if (error) return <ErrorState error={error} />;

  return (
    <div className="flex h-full w-full flex-col">
      {createError && (
        <div className="flex shrink-0 items-center gap-2 border-b border-red-200 bg-red-50 px-4 py-1.5 text-[12px] text-red-800">
          <span className="flex-1">Couldn't create event: {createError.message}</span>
          {createError.reconnectUrl && (
            <a
              href={createError.reconnectUrl}
              className="shrink-0 rounded bg-accent px-2.5 py-0.5 font-medium text-white hover:bg-accent-hover"
            >
              Reconnect
            </a>
          )}
        </div>
      )}
      <Scheduler
        dataSource={dataSource}
        views={SCHEDULER_VIEWS}
        currentView={currentView as SchedulerTypes.ViewType}
        currentDate={currentDate}
        editing={{
          allowAdding: true,
          allowUpdating: true,
          allowDeleting: false,
          allowDragging: true,
          allowResizing: true,
          allowTimeZoneEditing: false,
        }}
        height="100%"
        startDayHour={settings?.calendarStartHour ?? 5}
        endDayHour={settings?.calendarEndHour ?? 19}
        onAppointmentAdding={handleAdding}
        onAppointmentUpdating={handleUpdating}
        appointmentDragging={{ group: DRAG_GROUP, onAdd: handleChipDrop }}
        onOptionChanged={(e) => {
          if (e.name === "currentDate") setCurrentDate(e.value as Date);
          if (e.name === "currentView") setCurrentView(e.value as string);
        }}
      >
        <Toolbar>
          <Item name="dateNavigator" location="before" />
          <Item location="before" render={renderPalette} />
          <Item name="viewSwitcher" location="after" />
        </Toolbar>
        <Resource
          fieldExpr="accountId"
          dataSource={accountPalette}
          useColorAsDefault
          label="Account"
        />
      </Scheduler>
    </div>
  );
}
